import { Box, Button, List, ListItem, Skeleton, TextField, Typography } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { FC } from 'react';
import { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useAdSettings } from '../api';
import { useFormContext } from 'react-hook-form';
import useAdSetsData from '../hooks/useAdSetsData';

type LocalizedTextMap = Record<string, string>;

interface AdSetting {
  flow_name: string;
  platform: string;
  link: string;
  call_to_action: string;
  original_text: string;
  localized_text: LocalizedTextMap;
}

const AdSettingsSkeleton: FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: 2,
      pt: 2,
      pb: 4,
      width: '100%',
    }}
  >
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'start',
        maxWidth: 400,
      }}
    >
      <Skeleton
        width={'50%'}
        height={'28px'}
        sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
      />
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'start',
        flex: 4,
        maxWidth: 800,
        gap: 3,
      }}
    >
      <Skeleton
        width={'100%'}
        height={'40px'}
        sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
      />
      <Skeleton
        width={'100%'}
        height={'40px'}
        sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
      />
      <Skeleton
        width={'100%'}
        height={'40px'}
        sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
      />
    </Box>
  </Box>
);

const ReadOnlyTextField: FC<{
  label: string;
  value: string;
  maxWidth?: number;
}> = memo(({ label, value, maxWidth = 800 }) => (
  <TextField
    label={label}
    value={value}
    fullWidth
    sx={{
      maxWidth,
      '& .MuiOutlinedInput-root': {
        '&': {
          cursor: 'default',
        },
        '&:hover fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.23)',
          cursor: 'default',
        },
        '&.Mui-focused fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.23)',
          borderWidth: '1px',
        },
      },
    }}
    InputProps={{ readOnly: true }}
    InputLabelProps={{ shrink: true }}
  />
));

ReadOnlyTextField.displayName = 'ReadOnlyTextField';

const LocalizedText: FC<{
  localizedText: LocalizedTextMap;
  showAll?: boolean;
}> = memo(({ localizedText, showAll = false }) => {
  const languages = useMemo(() => Object.keys(localizedText), [localizedText]);
  const displayedLanguages = showAll ? languages : [];

  if (displayedLanguages.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}
    >
      {displayedLanguages.map((lang) => (
        <ReadOnlyTextField
          key={`${lang}_${localizedText[lang]}`}
          label={`Text ${lang.toUpperCase()}`}
          value={localizedText[lang]}
        />
      ))}
    </Box>
  );
});

LocalizedText.displayName = 'LocalizedText';

const AdSettingItem: FC<{
  children: React.ReactNode;
}> = memo(({ children }) => (
  <ListItem
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 4,
      px: 0,
      width: '100%',
    }}
  >
    {children}
  </ListItem>
));

AdSettingItem.displayName = 'AdSettingItem';

interface AdSettingProps {
  adSetting: AdSetting;
}

const AdSetting: FC<AdSettingProps> = memo(({ adSetting }) => {
  const [showLocalizedText, setShowLocalizedText] = useState(false);

  const hasLocalizedText = useMemo(
    () => Object.keys(adSetting.localized_text).length > 0,
    [adSetting.localized_text],
  );

  const handleToggleLocalizedText = () => {
    setShowLocalizedText((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {adSetting.flow_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {adSetting.platform}
        </Typography>
      </Box>

      <Box sx={{ flex: 4 }}>
        <List
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 0 }}
          disablePadding
        >
          <AdSettingItem>
            <ReadOnlyTextField
              label="Call to Action"
              value={adSetting.call_to_action}
            />
            <ReadOnlyTextField label="Link" value={adSetting.link} />
          </AdSettingItem>

          <AdSettingItem>
            <ReadOnlyTextField
              label="Original Text"
              value={adSetting.original_text}
            />
          </AdSettingItem>

          {hasLocalizedText && (
            <>
              {showLocalizedText && (
                <AdSettingItem>
                  <LocalizedText
                    localizedText={adSetting.localized_text}
                    showAll={showLocalizedText}
                  />
                </AdSettingItem>
              )}

              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={handleToggleLocalizedText}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: showLocalizedText
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                }
                sx={{ alignSelf: 'flex-start' }}
              >
                {showLocalizedText
                  ? 'Hide Localized Texts'
                  : 'Show Localized Texts'}
              </Button>
            </>
          )}
        </List>
      </Box>
    </Box>
  );
});

AdSetting.displayName = 'AdSetting';

const AdSettingsContent: FC = () => {
  const { watch, setError, setValue, clearErrors, trigger } = useFormContext();
  const app: string = watch('app');
  const { selectedAdSetsNames } = useAdSetsData();

  const { data: adSettings, error, isSuccess } = useAdSettings({
    params: {
      app: app,
      adgroup_names: selectedAdSetsNames,
    },
  });

  useEffect(() => {
    if (error) {
      setError?.('$adSettingsData', {
        type: 'manual',
        message: 'Failed to load ad settings',
      });
      void trigger?.();
      return;
    }

    if (!error && isSuccess) {
      clearErrors('$adSettingsData');
      setValue?.('$adSettingsData', adSettings, { shouldValidate: true });
    }

    return () => {
      clearErrors('$adSettingsData');
      setValue?.('$adSettingsData', [], { shouldValidate: true });
    };
  }, [error, trigger, clearErrors, setValue, setError, isSuccess, adSettings]);

  const adSettingsList = useMemo(() => {
    if (!adSettings?.length) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No ad settings found
          </Typography>
        </Box>
      );
    }

    return adSettings.map((adSetting) => (
      <AdSetting
        key={`${adSetting.flow_name}_${adSetting.platform}`}
        adSetting={adSetting}
      />
    ));
  }, [adSettings]);

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          Failed to load ad settings
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      {adSettingsList}
    </Box>
  );
};

const AdSettings: FC = memo(() => {
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Ad Settings
      </Typography>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Suspense fallback={<AdSettingsSkeleton />}>
          <AdSettingsContent />
        </Suspense>
      </Box>
    </Box>
  );
});

AdSettings.displayName = 'AdSettings';

export default AdSettings;
